import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { 
  DocumentTextIcon, 
  CheckCircleIcon,
  ExclamationCircleIcon,
  UploadIcon 
} from '../../components/common/Icons';
import { compressPaymentScreenshot, validatePaymentScreenshot, formatFileSize } from '../../utils/imageCompression';

const FeesDashboard = () => {
  // const { user } = useAuth(); // user not currently used
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [feeData, setFeeData] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [screenshotUrl, setScreenshotUrl] = useState(null);
  const [showScreenshot, setShowScreenshot] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [compressionInfo, setCompressionInfo] = useState(null);
  const [isPaymentInstructionsExpanded, setIsPaymentInstructionsExpanded] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amountPaid: '',
    paymentMode: 'UPI',
    feeCategory: 'TUITION',
    feeDescription: '',
    upiTransactionId: '',
    remarks: '',
    screenshot: null
  });

  useEffect(() => {
    fetchFeeData();
    fetchRecentTransactions();
  }, []);

  const fetchFeeData = async () => {
    try {
      const response = await api.get('/fees/pending');
      setFeeData(response.data.data);
    } catch (error) {
      console.error('Fetch fee data error:', error);
      toast.error('Failed to load fee information');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentTransactions = async () => {
    try {
      const response = await api.get('/fees/history?limit=5');
      setRecentTransactions(response.data.data.transactions);
    } catch (error) {
      console.error('Fetch transactions error:', error);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) {
      setPaymentForm({...paymentForm, screenshot: null});
      setCompressionInfo(null);
      return;
    }

    // Quick validation
    const validation = validatePaymentScreenshot(file);
    if (!validation.isValid) {
      toast.error(validation.error);
      e.target.value = '';
      return;
    }

    // Compress image
    setCompressing(true);
    try {
      const result = await compressPaymentScreenshot(file);
      
      if (!result.isValid) {
        toast.error(result.error);
        e.target.value = '';
        setCompressionInfo(null);
        return;
      }

      setPaymentForm({...paymentForm, screenshot: result.file});
      setCompressionInfo({
        originalSize: result.originalSize,
        compressedSize: result.size,
        compressionRatio: result.compressionRatio
      });

      if (result.compressionRatio) {
        toast.success(`Image compressed: ${formatFileSize(result.originalSize)} → ${formatFileSize(result.size)} (${result.compressionRatio}% smaller)`);
      }
    } catch (error) {
      console.error('Compression error:', error);
      toast.error('Failed to process image');
      e.target.value = '';
    } finally {
      setCompressing(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    
    if (!paymentForm.screenshot) {
      toast.error('Please upload payment screenshot');
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    formData.append('amountPaid', paymentForm.amountPaid);
    formData.append('paymentMode', paymentForm.paymentMode);
    formData.append('feeCategory', paymentForm.feeCategory);
    if (paymentForm.feeDescription) {
      formData.append('feeDescription', paymentForm.feeDescription);
    }
    formData.append('upiTransactionId', paymentForm.upiTransactionId);
    formData.append('remarks', paymentForm.remarks);
    formData.append('screenshot', paymentForm.screenshot);

    try {
      const response = await api.post('/fees/submit', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Show the official message from backend
      toast.success(response.data.message || 'Your payment will be verified by the Accounts Section. You will receive a receipt once verified.');
      
      setShowPaymentModal(false);
      setPaymentForm({
        amountPaid: '',
        paymentMode: 'UPI',
        feeCategory: 'TUITION',
        feeDescription: '',
        upiTransactionId: '',
        remarks: '',
        screenshot: null
      });
      setCompressionInfo(null);
      
      // Refresh data
      fetchFeeData();
      fetchRecentTransactions();
    } catch (error) {
      console.error('Payment submission error:', error);
      
      // Handle rate limit error (429)
      if (error.response?.status === 429) {
        const errorData = error.response.data;
        // eslint-disable-next-line no-unused-vars
        const hoursRemaining = errorData.hoursRemaining || 0;
        toast.error(
          `${errorData.message || 'Too many pending payments'}`,
          { autoClose: 8000 }
        );
      } else {
        toast.error(error.response?.data?.error || 'Failed to submit payment');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const downloadReceipt = async (transactionId, receiptNumber = null) => {
    try {
      const response = await api.get(`/fees/receipt/${transactionId}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = receiptNumber ? `${receiptNumber}.pdf` : `receipt-${transactionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Receipt downloaded successfully');
    } catch (error) {
      console.error('Download receipt error:', error);
      toast.error('Failed to download receipt');
    }
  };

  const viewScreenshot = async (transactionId) => {
    try {
      const response = await api.get(`/fees/screenshot/${transactionId}`);
      
      // Check if response contains presigned URL
      if (response.data.data && response.data.data.viewUrl) {
        // Open presigned URL in new tab
        window.open(response.data.data.viewUrl, '_blank');
      } else {
        // Fallback to blob (for backward compatibility)
        const blobResponse = await api.get(`/fees/screenshot/${transactionId}`, {
          responseType: 'blob'
        });
        const blob = new Blob([blobResponse.data]);
        const url = window.URL.createObjectURL(blob);
        setScreenshotUrl(url);
        setShowScreenshot(true);
      }
    } catch (error) {
      console.error('View screenshot error:', error);
      const errorMsg = error.response?.data?.error || 'Failed to view screenshot';
      toast.error(errorMsg);
    }
  };

  const closeScreenshot = () => {
    if (screenshotUrl) {
      window.URL.revokeObjectURL(screenshotUrl);
      setScreenshotUrl(null);
    }
    setShowScreenshot(false);
  };

  const getPaymentModeLabel = (mode) => {
    const modes = {
      CASH: 'Cash',
      CARD: 'Card',
      UPI: 'UPI',
      ONLINE: 'Online',
      NET_BANKING: 'Net Banking',
      CHEQUE: 'Cheque',
      DD: 'DD'
    };
    return modes[mode] || mode;
  };

  const getFeeCategoryLabel = (category, description) => {
    const categories = {
      APPLICATION_PROSPECTUS: 'Application Fee',
      TUITION: 'Tuition Fee',
      COLLEGE: 'College Fee',
      TRANSPORT: 'Transport Fee',
      HOSTEL: 'Hostel Fee',
      VTU_REGN: 'VTU Registration',
      VTU_EXAM: 'VTU Exam Fee',
      VTU_REVAL: 'VTU Reval/Backlog',
      OTHER: description || 'Other Fee'
    };
    return categories[category] || category;
  };

  const getStatusBadge = (status) => {
    const styles = {
      COMPLETED: 'bg-green-100 text-green-800',
      PENDING: 'bg-yellow-100 text-yellow-800',
      FAILED: 'bg-red-100 text-red-800'
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const getVerificationBadge = (status) => {
    const styles = {
      APPROVED: 'bg-green-100 text-green-800',
      COMPLETED: 'bg-green-100 text-green-800',
      PENDING_VERIFICATION: 'bg-yellow-100 text-yellow-800',
      PENDING: 'bg-yellow-100 text-yellow-800',
      REJECTED: 'bg-red-100 text-red-800',
      EXPIRED: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      APPROVED: 'Approved ✓',
      COMPLETED: 'Completed ✓',
      PENDING_VERIFICATION: 'Pending Verification',
      PENDING: 'Pending',
      REJECTED: 'Rejected ✗',
      EXPIRED: 'Expired'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${styles[status] || styles.PENDING}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-8 max-w-7xl">
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Fee Management</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Track and manage your fee payments</p>
        </div>
        <button
          onClick={() => setShowPaymentModal(true)}
          className="bg-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-blue-700 active:scale-95 transition-all flex items-center gap-2 w-full sm:w-auto justify-center text-sm sm:text-base"
        >
          <UploadIcon className="w-4 h-4 sm:w-5 sm:h-5" />
          Submit Payment
        </button>
      </div>

      {/* Payment Limits Info Banner */}
      {recentTransactions.filter(t => t.status === 'PENDING_VERIFICATION').length >= 2 && (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6 rounded-r-lg">
          <div className="flex items-start">
            <ExclamationCircleIcon className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800">Payment Request Limit</h3>
              <div className="mt-1 text-sm text-amber-700">
                <p>You have {recentTransactions.filter(t => t.status === 'PENDING_VERIFICATION').length} pending payment(s). Maximum is 3.</p>
                <p className="mt-1 text-xs">If you reach the limit, you can create 1 more request after waiting 24 hours from your last submission.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pending Amount Card - Single Card */}
      <div className={`bg-gradient-to-br ${
        feeData?.pendingAmount > 0 ? 'from-red-500 to-red-600' : 'from-green-500 to-green-600'
      } rounded-lg shadow-lg p-4 mb-6 text-white`}>
        <div className="flex items-center justify-between">
          <div>
            <span className="text-sm opacity-90 block mb-1">Pending Amount</span>
            <p className="text-3xl font-bold">₹{feeData?.pendingAmount?.toLocaleString() || '0'}</p>
            <p className="text-xs opacity-90 mt-1">
              {feeData?.pendingAmount > 0 ? 'Payment Due' : 'All Paid'}
            </p>
          </div>
          {feeData?.pendingAmount > 0 ? (
            <ExclamationCircleIcon className="w-12 h-12 opacity-80" />
          ) : (
            <CheckCircleIcon className="w-12 h-12 opacity-80" />
          )}
        </div>
      </div>

      {/* Payment Instructions - Collapsible */}
      <div className="bg-white rounded-lg shadow-sm mb-4 sm:mb-6 border-2 border-yellow-400 overflow-hidden">
        <button
          onClick={() => setIsPaymentInstructionsExpanded(!isPaymentInstructionsExpanded)}
          className="w-full px-3 sm:px-4 py-3 sm:py-3.5 flex items-center justify-between bg-gradient-to-r from-yellow-50 to-amber-50 hover:from-yellow-100 hover:to-amber-100 transition-colors"
        >
          <div className="flex items-center gap-2 sm:gap-3">
            <svg className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            <div className="text-left">
              <span className="font-bold text-yellow-900 text-xs sm:text-sm block">How to Pay Your Fees</span>
              <span className="text-xs text-yellow-700 font-medium">Step-by-step payment guide</span>
            </div>
          </div>
          <svg 
            className={`w-5 h-5 text-yellow-700 transition-transform flex-shrink-0 ${isPaymentInstructionsExpanded ? 'rotate-180' : ''}`}
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        
        {isPaymentInstructionsExpanded && (
          <div className="px-3 sm:px-4 py-4 bg-gradient-to-r from-yellow-50 to-amber-50 border-t-2 border-yellow-200">
            {/* Image floated to right */}
            <div className="hidden lg:block float-right ml-6 mb-4">
              <div className="bg-white border-2 border-dashed border-yellow-300 rounded-lg p-2 inline-flex items-center justify-center">
                <img 
                  src="/sample payment.png"
                  alt="Sample Payment Screenshot"
                  className="rounded-lg border border-gray-200 shadow-sm object-contain"
                  style={{ maxHeight: '250px', width: 'auto' }}
                  onLoad={(e) => {
                    e.target.nextElementSibling.style.display = 'none';
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'block';
                  }}
                />
                <div style={{ display: 'none' }} className="bg-gray-100 rounded-lg p-6">
                  <div className="text-4xl mb-2">📱</div>
                  <div className="text-sm text-gray-600 font-medium">Sample Payment Screenshot</div>
                  <div className="text-xs text-gray-500 mt-1">Shows Transaction ID, Amount, Date & Time</div>
                </div>
              </div>
            </div>
            
            {/* Payment Steps */}
            <div className="space-y-2 text-sm text-yellow-800">
              <div className="flex items-start gap-2">
                <span className="bg-yellow-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-0.5">1</span>
                <span><strong>SCAN QR Code</strong> near the Accounts Section with any UPI Payment app (GPay, PhonePe, Paytm, etc.)</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="bg-yellow-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-0.5">2</span>
                <span><strong>Pay your fee amount</strong> using your preferred UPI app</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="bg-yellow-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-0.5">3</span>
                <span><strong>Copy the Transaction ID</strong> from the payment success screen</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="bg-yellow-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-0.5">4</span>
                <span><strong>Take a screenshot</strong> of the complete payment details (see sample →)</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="bg-yellow-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-0.5">5</span>
                <span><strong>Fill the form</strong> with payment details and upload the screenshot</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="bg-yellow-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-semibold flex-shrink-0 mt-0.5">6</span>
                <span><strong>Submit and wait</strong> for the Accounts Section to verify and approve your payment</span>
              </div>
            </div>
            <div className="mt-3 p-3 bg-orange-100 border border-orange-300 rounded-lg text-xs text-orange-800 clear-right">
              <strong>⚠️ Important:</strong> Ensure your screenshot shows all payment details clearly for quick verification!
            </div>
            
            {/* Image for mobile only */}
            <div className="lg:hidden mt-4 flex justify-center">
              <div className="bg-white border-2 border-dashed border-yellow-300 rounded-lg p-2 inline-flex items-center justify-center">
                <img 
                  src="/sample payment.png"
                  alt="Sample Payment Screenshot"
                  className="rounded-lg border border-gray-200 shadow-sm object-contain"
                  style={{ maxHeight: '250px', width: 'auto' }}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recent Transactions */}
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">Recent Transactions</h2>
          <button
            onClick={() => navigate('/fees/history')}
            className="text-blue-600 hover:text-blue-700 active:text-blue-800 font-medium text-xs sm:text-sm"
          >
            View All →
          </button>
        </div>

        {recentTransactions.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <DocumentTextIcon className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-2 text-gray-300" />
            <p className="text-sm sm:text-base">No transactions found</p>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="block md:hidden space-y-4">
              {recentTransactions.map((txn) => (
                <div 
                  key={txn.id} 
                  className="border rounded-lg p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => {
                    setSelectedTransaction(txn);
                    setShowTransactionModal(true);
                  }}
                >
                  {/* Receipt Number - Clickable */}
                  {txn.receiptNumber && (
                    <div className="mb-2">
                      <span className="text-xs font-mono text-blue-600 font-medium">
                        {txn.receiptNumber}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="text-sm text-gray-500">
                        {new Date(txn.createdAt).toLocaleDateString()}
                      </p>
                      <p className="text-xl font-bold text-gray-900 mt-1">
                        ₹{parseFloat(txn.amountPaid).toLocaleString()}
                      </p>
                    </div>
                    {getVerificationBadge(txn.status)}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                    <div>
                      <span className="text-gray-500">Payment Mode:</span>
                      <p className="font-medium text-gray-900">{getPaymentModeLabel(txn.paymentMode)}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Category:</span>
                      <p className="font-medium text-gray-900">{getFeeCategoryLabel(txn.feeCategory, txn.feeDescription)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadge(txn.status)}`}>
                      {txn.status}
                    </span>
                    <div className="flex gap-2">
                      {(txn.status === 'APPROVED' || txn.status === 'COMPLETED') && txn.receiptNumber && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadReceipt(txn.id, txn.receiptNumber);
                          }}
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Receipt
                        </button>
                      )}
                      {txn.paymentScreenshot && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            viewScreenshot(txn.id);
                          }}
                          className="inline-flex items-center gap-1 text-green-600 hover:text-green-700 text-sm font-medium"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receipt No</th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Mode</th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {recentTransactions.map((txn) => (
                    <tr 
                      key={txn.id} 
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => {
                        setSelectedTransaction(txn);
                        setShowTransactionModal(true);
                      }}
                    >
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-mono text-blue-600">
                        {txn.receiptNumber || txn.receiptNo || `R${String(txn.id).padStart(6, '0')}`}
                      </td>
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(txn.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        ₹{parseFloat(txn.amountPaid).toLocaleString()}
                      </td>
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {getPaymentModeLabel(txn.paymentMode)}
                      </td>
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {getFeeCategoryLabel(txn.feeCategory, txn.feeDescription)}
                      </td>
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadge(txn.status)}`}>
                          {txn.status}
                        </span>
                      </td>
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-3">
                          {(txn.status === 'APPROVED' || txn.status === 'COMPLETED') && txn.receiptNumber && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadReceipt(txn.id, txn.receiptNumber);
                              }}
                              className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-700 font-medium transition-colors"
                              title="Download Receipt"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Receipt
                            </button>
                          )}
                          {txn.paymentScreenshot && (
                            <button
                              onClick={() => viewScreenshot(txn.id)}
                              className="inline-flex items-center gap-1.5 text-green-600 hover:text-green-700 font-medium transition-colors"
                              title="View Screenshot"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              View
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Payment Submission Modal */}
      {showPaymentModal && (
        <div 
          className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 sm:p-6 z-50 overflow-y-auto"
          onClick={() => setShowPaymentModal(false)}
        >
          <div 
            className="bg-white rounded-lg w-full max-w-4xl shadow-lg mt-4 sm:mt-0"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white/80 backdrop-blur-md rounded-t-lg">
              <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Submit Payment
              </h2>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="text-gray-500 hover:text-gray-700 rounded-full p-1 transition"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4 sm:p-6">
              <form onSubmit={handlePaymentSubmit}>
                <div className="space-y-4 sm:space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Amount Paid *
                    </label>
                    <input
                      type="number"
                      required
                      value={paymentForm.amountPaid}
                      onChange={(e) => setPaymentForm({...paymentForm, amountPaid: e.target.value})}
                      className="w-full px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                      placeholder="Enter amount"
                      min="1"
                      step="0.01"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Mode *
                    </label>
                    <select
                      value={paymentForm.paymentMode}
                      onChange={(e) => setPaymentForm({...paymentForm, paymentMode: e.target.value})}
                      className="w-full px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                    >
                      <option value="UPI">UPI</option>
                      <option value="ONLINE">Online Banking</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fee Category *
                    </label>
                    <select
                      value={paymentForm.feeCategory}
                      onChange={(e) => setPaymentForm({...paymentForm, feeCategory: e.target.value, feeDescription: e.target.value !== 'OTHER' ? '' : paymentForm.feeDescription})}
                      className="w-full px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                    >
                      <option value="APPLICATION_PROSPECTUS">Application / Prospectus Fee</option>
                      <option value="TUITION">Tuition Fee</option>
                      <option value="COLLEGE">College Fee</option>
                      <option value="TRANSPORT">Transport Fee</option>
                      <option value="HOSTEL">Hostel Fee</option>
                      <option value="VTU_REGN">VTU Registration Fee</option>
                      <option value="VTU_EXAM">VTU Regular Exam Fee</option>
                      <option value="VTU_REVAL">VTU Reval/Backlog Fee</option>
                      <option value="OTHER">Any Other Fee</option>
                    </select>
                  </div>

                  {paymentForm.feeCategory === 'OTHER' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Specify Fee Type *
                      </label>
                      <input
                        type="text"
                        required
                        value={paymentForm.feeDescription}
                        onChange={(e) => setPaymentForm({...paymentForm, feeDescription: e.target.value})}
                        className="w-full px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
                        placeholder="Enter fee description (e.g., Sports Fee, Lab Fee)"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Transaction ID *
                    </label>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 mb-2">
                      <p className="text-xs text-blue-800">
                        📱 <strong>Copy-paste</strong> the Transaction ID from your payment app (UPI/Banking app)
                      </p>
                    </div>
                    <input
                      type="text"
                      required
                      value={paymentForm.upiTransactionId}
                      onChange={(e) => setPaymentForm({...paymentForm, upiTransactionId: e.target.value})}
                      className="w-full px-3 sm:px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm sm:text-base font-mono"
                      placeholder="Paste transaction ID here"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Screenshot * (JPEG, PNG, WebP, PDF - Max 5MB)
                    </label>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-2">
                      <p className="font-medium mb-1 text-sm text-blue-900">Screenshot Requirements:</p>
                      <ul className="list-disc list-inside space-y-0.5 text-xs text-blue-800">
                        <li>Must show Transaction ID clearly</li>
                        <li>Minimum width: 480px for readability</li>
                        <li>Auto-compressed to under 1 MB</li>
                        <li>Clear and readable for verification</li>
                      </ul>
                    </div>
                    <input
                      type="file"
                      required
                      accept="image/jpeg,image/png,image/webp,application/pdf"
                      onChange={handleFileSelect}
                      disabled={compressing}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    />
                    {compressing && (
                      <p className="text-sm text-blue-600 mt-2 flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Compressing image...
                      </p>
                    )}
                    {paymentForm.screenshot && compressionInfo && (
                      <div className="text-sm mt-2 space-y-1">
                        <p className="text-green-600 font-medium">
                          ✓ {paymentForm.screenshot.name}
                        </p>
                        <p className="text-gray-600 text-xs">
                          Compressed: {formatFileSize(compressionInfo.originalSize)} → {formatFileSize(compressionInfo.compressedSize)}
                          {compressionInfo.compressionRatio && ` (${compressionInfo.compressionRatio}% reduction)`}
                        </p>
                      </div>
                    )}
                    {paymentForm.screenshot && !compressionInfo && (
                      <p className="text-sm text-green-600 mt-1">
                        ✓ {paymentForm.screenshot.name} ({formatFileSize(paymentForm.screenshot.size)})
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Remarks (Optional)
                    </label>
                    <textarea
                      value={paymentForm.remarks}
                      onChange={(e) => setPaymentForm({...paymentForm, remarks: e.target.value})}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      rows="3"
                      placeholder="Any additional information"
                    />
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 mt-4">
                  <p className="text-xs sm:text-sm text-yellow-800">
                    <strong>Note:</strong> Your payment will be verified by the Accounts Section. You will receive a receipt once verified.
                  </p>
                </div>

                <div className="flex flex-col-reverse sm:flex-row gap-3 sm:gap-4 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowPaymentModal(false)}
                    className="w-full sm:w-auto px-4 sm:px-6 py-3 border-2 border-gray-300 rounded-lg hover:bg-gray-50 active:bg-gray-100 font-medium text-sm sm:text-base transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting || compressing}
                    className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 sm:px-6 py-3 rounded-lg hover:from-blue-700 hover:to-indigo-700 active:scale-[0.98] disabled:from-gray-400 disabled:to-gray-400 font-semibold text-sm sm:text-base transition-all flex items-center justify-center gap-2 shadow-lg"
                  >
                    {submitting ? (
                      <>
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Submit Payment
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Screenshot Modal */}
      {showScreenshot && screenshotUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4" onClick={closeScreenshot}>
          <div className="relative max-w-6xl max-h-[90vh] w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={closeScreenshot}
              className="absolute top-4 right-4 text-white bg-black bg-opacity-50 hover:bg-opacity-75 rounded-full p-2 z-10"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img
              src={screenshotUrl}
              alt="Payment Screenshot"
              className="w-full h-full object-contain rounded-lg"
            />
          </div>
        </div>
      )}

      {/* Transaction Details Modal */}
      {showTransactionModal && selectedTransaction && (
        <div 
          className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto"
          onClick={() => setShowTransactionModal(false)}
        >
          <div 
            className="bg-white rounded-lg w-full max-w-2xl shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white/80 backdrop-blur-md rounded-t-lg">
              <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Transaction Details
              </h2>
              <button
                onClick={() => setShowTransactionModal(false)}
                className="text-gray-500 hover:text-gray-700 rounded-full p-1 transition"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-4 sm:p-6">
              {/* Mobile Card Layout */}
              <div className="block sm:hidden space-y-4">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="font-semibold text-blue-900">Receipt Details</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Sl. No.</p>
                      <p className="font-mono text-blue-600 font-semibold">
                        {selectedTransaction.receiptNumber || selectedTransaction.receiptNo || `R${String(selectedTransaction.id).padStart(6, '0')}`}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Date</p>
                      <p className="text-gray-900">{new Date(selectedTransaction.createdAt).toLocaleDateString('en-GB')}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Amount Paid</p>
                      <p className="text-lg font-bold text-green-600">₹{parseFloat(selectedTransaction.amountPaid).toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <h3 className="font-semibold text-green-900">Student Details</h3>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Name</p>
                      <p className="text-gray-900 font-semibold">{selectedTransaction.user?.name || 'N/A'}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">USN</p>
                        <p className="text-gray-900 font-mono">{selectedTransaction.user?.usn || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Branch</p>
                        <p className="text-gray-900">{selectedTransaction.user?.department || 'N/A'}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Year/Semester</p>
                      <p className="text-gray-900">{selectedTransaction.user?.semester?.replace('SEM_', 'Semester ') || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200">
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    <h3 className="font-semibold text-purple-900">Payment Information</h3>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Mode</p>
                        <p className="text-gray-900">{getPaymentModeLabel(selectedTransaction.paymentMode)}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Status</p>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadge(selectedTransaction.status)}`}>
                          {selectedTransaction.status}
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Fee Category</p>
                      <p className="text-gray-900">{getFeeCategoryLabel(selectedTransaction.feeCategory, selectedTransaction.feeDescription)}</p>
                    </div>
                    {selectedTransaction.upiTransactionId && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction ID</p>
                        <p className="text-gray-900 font-mono bg-gray-100 px-2 py-1 rounded text-xs break-all">
                          {selectedTransaction.upiTransactionId}
                        </p>
                      </div>
                    )}
                    {selectedTransaction.previousDue && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Previous Due</p>
                        <p className="text-gray-900 font-semibold">₹{parseFloat(selectedTransaction.previousDue).toLocaleString()}</p>
                      </div>
                    )}
                    {selectedTransaction.amountDue && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Amount Due</p>
                        <p className="text-gray-900 font-semibold">₹{parseFloat(selectedTransaction.amountDue).toLocaleString()}</p>
                      </div>
                    )}
                    {selectedTransaction.remarks && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</p>
                        <p className="text-gray-900">{selectedTransaction.remarks}</p>
                      </div>
                    )}
                    {selectedTransaction.verificationRemarks && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Verification Remarks</p>
                        <p className="text-gray-900 bg-yellow-50 border border-yellow-200 rounded px-2 py-1">{selectedTransaction.verificationRemarks}</p>
                      </div>
                    )}
                  </div>
                </div>

                {selectedTransaction.screenshot && (
                  <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-4 border border-yellow-200">
                    <div className="flex items-center gap-2 mb-3">
                      <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <h3 className="font-semibold text-yellow-900">Payment Screenshot</h3>
                    </div>
                    <img 
                      src={selectedTransaction.screenshot} 
                      alt="Payment Screenshot"
                      className="w-full h-auto rounded cursor-pointer hover:shadow-lg transition-shadow"
                      onClick={() => {
                        setScreenshotUrl(selectedTransaction.screenshot);
                        setShowScreenshot(true);
                      }}
                    />
                    <p className="text-xs text-gray-500 text-center mt-2">Tap to view full size</p>
                  </div>
                )}
              </div>

              {/* Desktop Grid Layout */}
              <div className="hidden sm:block">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {/* Receipt Information */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900 text-sm uppercase tracking-wider border-b pb-2">Receipt Details</h3>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Sl. No.</label>
                      <p className="text-sm font-mono text-blue-600 font-semibold">
                        {selectedTransaction.receiptNumber || selectedTransaction.receiptNo || `R${String(selectedTransaction.id).padStart(6, '0')}`}
                      </p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Date</label>
                      <p className="text-sm text-gray-900">{new Date(selectedTransaction.createdAt).toLocaleDateString('en-GB')}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Amount Paid</label>
                      <p className="text-lg font-bold text-green-600">₹{parseFloat(selectedTransaction.amountPaid).toLocaleString()}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Mode</label>
                      <p className="text-sm text-gray-900">{getPaymentModeLabel(selectedTransaction.paymentMode)}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Status</label>
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${getStatusBadge(selectedTransaction.status)}`}>
                        {selectedTransaction.status}
                      </span>
                    </div>
                  </div>

                  {/* Student & Payment Details */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-gray-900 text-sm uppercase tracking-wider border-b pb-2">Student Details</h3>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Name</label>
                      <p className="text-sm text-gray-900 font-semibold">{selectedTransaction.user?.name || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">USN</label>
                      <p className="text-sm font-mono text-gray-900">{selectedTransaction.user?.usn || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Branch</label>
                      <p className="text-sm text-gray-900">{selectedTransaction.user?.department || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Year/Semester</label>
                      <p className="text-sm text-gray-900">{selectedTransaction.user?.semester?.replace('SEM_', 'Semester ') || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Fee Category</label>
                      <p className="text-sm text-gray-900">{getFeeCategoryLabel(selectedTransaction.feeCategory, selectedTransaction.feeDescription)}</p>
                    </div>
                  </div>
                </div>

                {/* Additional Payment Information */}
                <div className="border-t pt-4 mb-4">
                  <h3 className="font-semibold text-gray-900 text-sm uppercase tracking-wider mb-4">Payment Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {selectedTransaction.upiTransactionId && (
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction ID</label>
                        <p className="text-sm font-mono text-gray-900 bg-gray-100 px-2 py-1 rounded break-all">
                          {selectedTransaction.upiTransactionId}
                        </p>
                      </div>
                    )}
                    {selectedTransaction.previousDue && (
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Previous Due</label>
                        <p className="text-sm text-gray-900 font-semibold">₹{parseFloat(selectedTransaction.previousDue).toLocaleString()}</p>
                      </div>
                    )}
                    {selectedTransaction.amountDue && (
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Amount Due</label>
                        <p className="text-sm text-gray-900 font-semibold">₹{parseFloat(selectedTransaction.amountDue).toLocaleString()}</p>
                      </div>
                    )}
                    {selectedTransaction.remarks && (
                      <div className="md:col-span-2">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</label>
                        <p className="text-sm text-gray-900">{selectedTransaction.remarks}</p>
                      </div>
                    )}
                    {selectedTransaction.verificationRemarks && (
                      <div className="md:col-span-2">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Verification Remarks (Accounts Section)</label>
                        <p className="text-sm text-gray-900 bg-yellow-50 border border-yellow-200 rounded px-3 py-2">{selectedTransaction.verificationRemarks}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Screenshot */}
                {selectedTransaction.screenshot && (
                  <div className="border-t pt-4">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wider block mb-2">Payment Screenshot</label>
                    <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-4">
                      <img 
                        src={selectedTransaction.screenshot} 
                        alt="Payment Screenshot"
                        className="max-w-full h-auto rounded cursor-pointer hover:shadow-lg transition-shadow"
                        onClick={() => {
                          setScreenshotUrl(selectedTransaction.screenshot);
                          setShowScreenshot(true);
                        }}
                      />
                      <p className="text-xs text-gray-500 text-center mt-2">Click to view full size</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowTransactionModal(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeesDashboard;
